import { useMemo, useRef, useSyncExternalStore } from "react"

import type { CollectionSource, CompletableCollection } from "../data/collection-source"
import type { RemoteCollectionHandle } from "../data/remote-collection"
import type { CommandFailureMessages, RemoteCommandFailure } from "../remote/remote-command"
import type { ApplicationActionContext } from "./application-action"
import { executeApplicationAction, useActionRuntime, type ActionRuntime } from "./application-action-runtime"
import { useApplicationScope } from "./application-provider"
import { useEditScopeDirty } from "./edit-scope"
import type { ApplicationScope } from "./plugin-runtime"
import { useConfirm, type Confirm } from "./use-confirm"

type MaybePromise<T> = T | Promise<T>

export type CollectionEditorRowStatus = "clean" | "dirty" | "saving" | "failed"

export interface CollectionEditorRowState {
  readonly status: CollectionEditorRowStatus
  /** Inserted with `insertRow` and not created on the server yet. */
  readonly isNew: boolean
  /** Messages per field (property name or column ID), from row validation or a failed save. */
  readonly cellErrors: Readonly<Partial<Record<string, readonly string[]>>>
  /** Messages that belong to the row as a whole. */
  readonly rowErrors: readonly string[]
}

export type CollectionEditorValidation = Readonly<Partial<Record<string, string | readonly string[]>>>

export interface CollectionEditorOptions<TItem> {
  /**
   * Saves one changed row inside its own application action. `row` is the server row the draft started from.
   * Return the saved row to show it at once; otherwise the editor refreshes that row before it ends the save.
   */
  save(row: Readonly<TItem>, draft: Readonly<TItem>, context: ApplicationActionContext): MaybePromise<TItem | void>
  /** Creates an inserted row. Afterwards the collection refreshes, because the new row's position is the server's. */
  create?(draft: Readonly<TItem>, context: ApplicationActionContext): MaybePromise<TItem | void>
  /** Deletes rows. Afterwards the collection refreshes. */
  remove?(rows: readonly Readonly<TItem>[], context: ApplicationActionContext): MaybePromise<void>
  /** Validates a draft before it is saved. Messages are keyed by field and block the save. */
  validateRow?(draft: Readonly<TItem>): CollectionEditorValidation | null | undefined
  /** Splits a failed save over fields and the row, usually with `mapCommandFailure`. Default: the display message. */
  mapFailure?(failure: RemoteCommandFailure): CommandFailureMessages<string>
  /** Ask before deleting rows that exist on the server. Default: true. */
  confirmRemove?: boolean
  /** Handles a failed delete. A failed save shows on its row instead. */
  onRemoveFailure?(failure: RemoteCommandFailure): MaybePromise<void>
  /** Handles a technical error of a delete. Errors are reported to telemetry automatically. */
  onRemoveError?(error: unknown): MaybePromise<void>
  /** Optional owner lifetime, for example a use-case signal. */
  readonly signal?: AbortSignal
}

export interface CollectionEditor<TItem> {
  /** The collection with drafts in place of their server rows and inserted rows first. Bind views to this. */
  readonly source: CollectionSource<TItem>
  /** Number of rows that are changed, inserted, saving or failed. */
  readonly changedCount: number
  readonly hasChanges: boolean
  getRowState(key: string): CollectionEditorRowState
  /** Changes fields of a row. A row that is saving is read-only and ignores the change. */
  update(row: Readonly<TItem>, changes: Partial<TItem>): void
  /** Validates and saves one row. Resolves true when the row is clean afterwards. */
  saveRow(key: string): Promise<boolean>
  /** Saves every changed row; rows save concurrently. Resolves true when all are clean afterwards. */
  saveAll(): Promise<boolean>
  /** Discards the draft of a row, or removes an inserted row. A row that is saving is not reverted. */
  revertRow(key: string): void
  revertAll(): void
  /** Adds a new row before the collection's rows and returns its temporary key. */
  insertRow(item: TItem): string
  /** Deletes rows: inserted rows at once, server rows after confirmation through `remove`. */
  removeRows(keys: readonly string[]): Promise<boolean>
  subscribe(listener: () => void): () => void
}

/**
 * Edits the rows of a remote collection and saves them to the server one row at a time.
 * Saving is pessimistic: a saving row keeps its draft, is read-only, and shows the server's row afterwards.
 * Rows save concurrently; one row never has two saves in flight.
 */
export function useCollectionEditor<TItem, TQuery, TSortField extends string>(
  collection: RemoteCollectionHandle<TItem, TQuery, TSortField>,
  options: CollectionEditorOptions<TItem>
): CollectionEditor<TItem> {
  const scope = useApplicationScope()
  const confirm = useConfirm()
  const getRuntime = useActionRuntime(options.signal)
  const servicesRef = useRef<EditorServices<TItem>>({ scope, confirm, getRuntime, options })
  servicesRef.current = { scope, confirm, getRuntime, options }
  const store = useMemo(() => new CollectionEditorStore(collection, servicesRef), [collection])
  const version = useSyncExternalStore(store.subscribeState, store.getVersion, store.getVersion)

  useEditScopeDirty(store.changedCount > 0)

  return useMemo(() => createFacade(store), [store, version])
}

interface EditorServices<TItem> {
  readonly scope: ApplicationScope
  readonly confirm: Confirm
  readonly getRuntime: () => ActionRuntime
  readonly options: CollectionEditorOptions<TItem>
}

interface RowEntry<TItem> {
  readonly key: string
  readonly isNew: boolean
  /** Server row the draft started from; the draft itself for an inserted row. */
  readonly base: TItem
  draft: TItem
  status: Exclude<CollectionEditorRowStatus, "clean">
  cellErrors: Partial<Record<string, readonly string[]>>
  rowErrors: readonly string[]
}

const cleanRowState: CollectionEditorRowState = Object.freeze({
  status: "clean",
  isNew: false,
  cellErrors: Object.freeze({}),
  rowErrors: Object.freeze([]),
})

const insertedKeyPrefix = "collection-editor-new:"

class CollectionEditorStore<TItem> {
  private readonly entries = new Map<string, RowEntry<TItem>>()
  /** Inserted rows, newest first. */
  private inserted: RowEntry<TItem>[] = []
  private readonly insertedKeys = new WeakMap<object, string>()
  private readonly stateListeners = new Set<() => void>()
  private readonly dataListeners = new Set<() => void>()
  private version = 0
  private dataVersion = 0
  private resultSetVersion = 0
  private nextInsertedId = 1
  readonly source: CollectionSource<TItem>

  constructor(
    private readonly collection: RemoteCollectionHandle<TItem, unknown, string>,
    private readonly services: { readonly current: EditorServices<TItem> }
  ) {
    this.source = this.createSource()
  }

  readonly getVersion = () => this.version

  readonly subscribeState = (listener: () => void) => {
    this.stateListeners.add(listener)
    return () => {
      this.stateListeners.delete(listener)
    }
  }

  get changedCount() {
    return this.entries.size + this.inserted.length
  }

  getRowState(key: string): CollectionEditorRowState {
    const entry = this.findEntry(key)
    if (entry == null) return cleanRowState
    return { status: entry.status, isNew: entry.isNew, cellErrors: entry.cellErrors, rowErrors: entry.rowErrors }
  }

  update(row: Readonly<TItem>, changes: Partial<TItem>) {
    const key = this.source.getKey(row as TItem)
    const existing = this.findEntry(key)
    if (existing?.status === "saving") return

    const entry: RowEntry<TItem> = existing ?? {
      key,
      isNew: false,
      base: row as TItem,
      draft: row as TItem,
      status: "dirty",
      cellErrors: {},
      rowErrors: [],
    }
    entry.draft = { ...entry.draft, ...changes }
    entry.status = "dirty"
    entry.rowErrors = []
    const cellErrors = { ...entry.cellErrors }
    for (const field of Object.keys(changes)) delete cellErrors[field]
    entry.cellErrors = cellErrors

    if (!entry.isNew && sameFields(entry.draft, entry.base)) this.entries.delete(key)
    else if (!entry.isNew) this.entries.set(key, entry)
    this.notify({ data: true })
  }

  async saveRow(key: string): Promise<boolean> {
    const entry = this.findEntry(key)
    if (entry == null) return true
    if (entry.status === "saving") return false

    const { options } = this.services.current
    const validation = normalizeValidation(options.validateRow?.(entry.draft))
    if (validation != null) {
      entry.status = "failed"
      entry.cellErrors = validation
      entry.rowErrors = []
      this.notify({ data: false })
      return false
    }

    entry.status = "saving"
    entry.cellErrors = {}
    entry.rowErrors = []
    this.notify({ data: false })

    const { scope, getRuntime } = this.services.current
    const draft = entry.draft
    const result = await executeApplicationAction(scope, getRuntime().controller.signal, (context) => {
      if (!entry.isNew) return options.save(entry.base, draft, context)
      if (options.create == null) throw new Error("A collection editor with inserted rows requires create.")
      return options.create(draft, context)
    })

    switch (result.status) {
      case "succeeded":
        await this.applySaved(entry, result.value)
        return true
      case "failed": {
        const messages = options.mapFailure?.(result.failure) ?? {
          form: result.failure.displayMessage == null ? [] : [result.failure.displayMessage],
          fields: {},
        }
        const hasFieldMessages = Object.values(messages.fields).some((value) => value != null && value.length > 0)
        this.fail(entry, messages.fields, messages.form.length > 0 || hasFieldMessages ? messages.form : [])
        return false
      }
      case "error":
        this.fail(entry, {}, [])
        return false
      case "aborted":
      case "rejected":
        if (this.isCurrent(entry)) {
          entry.status = "dirty"
          this.notify({ data: false })
        }
        return false
    }
  }

  async saveAll(): Promise<boolean> {
    const keys = [...this.inserted.map((entry) => entry.key), ...this.entries.keys()]
    const results = await Promise.all(keys.map((key) => this.saveRow(key)))
    return results.every(Boolean)
  }

  revertRow(key: string) {
    const entry = this.findEntry(key)
    if (entry == null || entry.status === "saving") return
    this.removeEntry(entry)
  }

  revertAll() {
    const changed = [...this.inserted, ...this.entries.values()].filter((entry) => entry.status !== "saving")
    if (changed.length === 0) return
    for (const entry of changed) this.removeEntry(entry, false)
    this.notify({ data: true, resultSet: changed.some((entry) => entry.isNew) })
  }

  insertRow(item: TItem): string {
    const key = `${insertedKeyPrefix}${this.nextInsertedId++}`
    const entry: RowEntry<TItem> = {
      key,
      isNew: true,
      base: item,
      draft: item,
      status: "dirty",
      cellErrors: {},
      rowErrors: [],
    }
    this.inserted = [entry, ...this.inserted]
    this.notify({ data: true, resultSet: true })
    return key
  }

  async removeRows(keys: readonly string[]): Promise<boolean> {
    const insertedKeys = new Set(this.inserted.map((entry) => entry.key))
    const newRows = keys.filter((key) => insertedKeys.has(key))
    const serverKeys = keys.filter((key) => !insertedKeys.has(key))
    if (newRows.length > 0) {
      this.inserted = this.inserted.filter((entry) => !newRows.includes(entry.key) || entry.status === "saving")
      this.notify({ data: true, resultSet: true })
    }
    if (serverKeys.length === 0) return true

    const rows = this.findServerRows(serverKeys)
    if (rows.length === 0) return true
    const { options, scope, confirm, getRuntime } = this.services.current
    if (options.remove == null) throw new Error("Deleting rows of a collection editor requires remove.")
    const signal = getRuntime().controller.signal
    if (options.confirmRemove !== false) {
      const message = scope.localization.i18n.t(($) => $.editing.removeRows, { ns: "florisoft-framework" })
      if (!(await confirm({ message, destructive: true }, signal))) return false
    }

    const remove = options.remove
    const result = await executeApplicationAction(scope, signal, (context) => remove(rows, context), {
      onFailure: (failure) => this.services.current.options.onRemoveFailure?.(failure),
      onError: (error) => this.services.current.options.onRemoveError?.(error),
    })
    if (result.status !== "succeeded") return false

    for (const key of serverKeys) this.entries.delete(key)
    const removed = new Set(serverKeys)
    const selected = this.collection.selectedKeys.filter((key) => !removed.has(key))
    if (selected.length !== this.collection.selectedKeys.length) this.collection.setSelectedKeys(selected)
    this.notify({ data: true })
    await this.collection.refresh().catch(() => undefined)
    return true
  }

  private async applySaved(entry: RowEntry<TItem>, saved: TItem | void) {
    if (entry.isNew) {
      // Keep the saving row until the refreshed collection shows the created one.
      await this.collection.refresh().catch(() => undefined)
      if (this.isCurrent(entry)) this.removeEntry(entry)
      return
    }
    if (saved !== undefined) {
      this.collection.replaceItems([saved])
    } else {
      await this.collection.refreshWhere((item) => this.collection.getKey(item) === entry.key).catch(() => undefined)
    }
    if (this.isCurrent(entry)) this.removeEntry(entry)
  }

  private fail(
    entry: RowEntry<TItem>,
    cellErrors: Partial<Record<string, readonly string[]>>,
    rowErrors: readonly string[]
  ) {
    if (!this.isCurrent(entry)) return
    const hasCellErrors = Object.values(cellErrors).some((value) => value != null && value.length > 0)
    entry.status = "failed"
    entry.cellErrors = cellErrors
    entry.rowErrors =
      rowErrors.length > 0 || hasCellErrors
        ? rowErrors
        : [this.services.current.scope.localization.i18n.t(($) => $.errors.saveFailed, { ns: "florisoft-framework" })]
    this.notify({ data: false })
  }

  private findEntry(key: string): RowEntry<TItem> | undefined {
    return this.entries.get(key) ?? this.inserted.find((entry) => entry.key === key)
  }

  private isCurrent(entry: RowEntry<TItem>) {
    return this.findEntry(entry.key) === entry
  }

  private removeEntry(entry: RowEntry<TItem>, notify = true) {
    if (entry.isNew) this.inserted = this.inserted.filter((candidate) => candidate !== entry)
    else this.entries.delete(entry.key)
    if (notify) this.notify({ data: true, resultSet: entry.isNew })
  }

  private findServerRows(keys: readonly string[]): TItem[] {
    const wanted = new Set(keys)
    const rows = new Map<string, TItem>()
    for (const key of keys) {
      const entry = this.entries.get(key)
      if (entry != null) rows.set(key, entry.base)
    }
    for (const [, item] of this.collection.getLoadedEntries()) {
      const key = this.collection.getKey(item)
      if (wanted.has(key) && !rows.has(key)) rows.set(key, item)
    }
    return keys.flatMap((key) => {
      const row = rows.get(key)
      return row === undefined ? [] : [row]
    })
  }

  private notify(change: { readonly data: boolean; readonly resultSet?: boolean }) {
    this.version += 1
    if (change.data) this.dataVersion += 1
    if (change.resultSet) this.resultSetVersion += 1
    for (const listener of [...this.stateListeners]) listener()
    if (change.data) for (const listener of [...this.dataListeners]) listener()
  }

  private overlay(item: TItem | undefined): TItem | undefined {
    if (item === undefined) return undefined
    return this.entries.get(this.collection.getKey(item))?.draft ?? item
  }

  private createSource(): CollectionSource<TItem> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const store = this
    const collection = this.collection
    const offset = () => store.inserted.length

    const source: CollectionSource<TItem> = {
      get sorting() {
        return collection.sorting
      },
      get selectedKeys() {
        return collection.selectedKeys
      },
      get capabilities() {
        return collection.capabilities
      },
      get pageSize() {
        return collection.pageSize
      },
      get status() {
        return collection.status
      },
      get error() {
        return collection.error
      },
      get revision() {
        return collection.revision + store.version
      },
      get dataRevision() {
        return collection.dataRevision + store.dataVersion
      },
      get resultSetRevision() {
        return collection.resultSetRevision + store.resultSetVersion
      },
      get itemCount() {
        return collection.itemCount + offset()
      },
      get totalCount() {
        return collection.totalCount == null ? undefined : collection.totalCount + offset()
      },
      get loadedCount() {
        return collection.loadedCount + offset()
      },
      get isLoading() {
        return collection.isLoading
      },
      get(index) {
        const inserted = store.inserted
        if (index < inserted.length) return inserted[index]?.draft
        return store.overlay(collection.get(index - inserted.length))
      },
      *getLoadedEntries() {
        const inserted = store.inserted
        for (let index = 0; index < inserted.length; index++) yield [index, inserted[index]!.draft] as const
        for (const [index, item] of collection.getLoadedEntries()) {
          yield [index + inserted.length, store.overlay(item)!] as const
        }
      },
      getKey(item) {
        const insertedKey = typeof item === "object" && item != null ? store.findInsertedKey(item) : undefined
        return insertedKey ?? collection.getKey(item)
      },
      getItemState(index) {
        return index < offset() ? "loaded" : collection.getItemState(index - offset())
      },
      getItemError(index) {
        return index < offset() ? undefined : collection.getItemError(index - offset())
      },
      getItemErrorRange(index) {
        const shift = offset()
        if (index < shift) return undefined
        const range = collection.getItemErrorRange(index - shift)
        return range == null ? undefined : { start: range.start + shift, end: range.end + shift }
      },
      setSorting(sorting) {
        collection.setSorting(sorting)
      },
      setSelectedKeys(keys) {
        collection.setSelectedKeys(keys)
      },
      ensureRange(start, end) {
        const shift = offset()
        collection.ensureRange(Math.max(0, start - shift), Math.max(0, end - shift))
      },
      retryRange(start, end) {
        const shift = offset()
        return collection.retryRange(Math.max(0, start - shift), Math.max(0, end - shift))
      },
      subscribe(listener) {
        store.stateListeners.add(listener)
        const unsubscribe = collection.subscribe(listener)
        return () => {
          store.stateListeners.delete(listener)
          unsubscribe()
        }
      },
      subscribeData(listener) {
        store.dataListeners.add(listener)
        const unsubscribe = collection.subscribeData(listener)
        return () => {
          store.dataListeners.delete(listener)
          unsubscribe()
        }
      },
    }
    if (collection.completeness === undefined) return source

    // A completable collection stays completable through the editor: inserted rows first, drafts in place.
    let allItems: { readonly dataRevision: number; readonly items: readonly TItem[] } | undefined
    const completable: CompletableCollection<TItem> = {
      get completeness() {
        return collection.completeness!
      },
      requireComplete: () => collection.requireComplete(),
      getAllItems() {
        const items = collection.getAllItems()
        if (items === undefined) return undefined
        const dataRevision = source.dataRevision
        if (allItems?.dataRevision !== dataRevision) {
          allItems = {
            dataRevision,
            items: [...store.inserted.map((entry) => entry.draft), ...items.map((item) => store.overlay(item)!)],
          }
        }
        return allItems.items
      },
    }
    return Object.defineProperties(source, Object.getOwnPropertyDescriptors(completable))
  }

  private findInsertedKey(item: object): string | undefined {
    const cached = this.insertedKeys.get(item)
    if (cached != null) return cached
    const entry = this.inserted.find((candidate) => (candidate.draft as unknown) === item)
    if (entry != null) this.insertedKeys.set(item, entry.key)
    return entry?.key
  }
}

function createFacade<TItem>(store: CollectionEditorStore<TItem>): CollectionEditor<TItem> {
  return {
    source: store.source,
    changedCount: store.changedCount,
    hasChanges: store.changedCount > 0,
    getRowState: (key) => store.getRowState(key),
    update: (row, changes) => store.update(row, changes),
    saveRow: (key) => store.saveRow(key),
    saveAll: () => store.saveAll(),
    revertRow: (key) => store.revertRow(key),
    revertAll: () => store.revertAll(),
    insertRow: (item) => store.insertRow(item),
    removeRows: (keys) => store.removeRows(keys),
    subscribe: (listener) => store.subscribeState(listener),
  }
}

function normalizeValidation(
  validation: CollectionEditorValidation | null | undefined
): Partial<Record<string, readonly string[]>> | undefined {
  if (validation == null) return undefined
  const errors: Partial<Record<string, readonly string[]>> = {}
  for (const [field, value] of Object.entries(validation)) {
    if (value == null) continue
    const messages = typeof value === "string" ? [value] : value
    if (messages.length > 0) errors[field] = messages
  }
  return Object.keys(errors).length > 0 ? errors : undefined
}

function sameFields<TItem>(left: TItem, right: TItem): boolean {
  if (left === right) return true
  if (typeof left !== "object" || typeof right !== "object" || left == null || right == null) return false
  const leftRecord = left as Record<string, unknown>
  const rightRecord = right as Record<string, unknown>
  const keys = new Set([...Object.keys(leftRecord), ...Object.keys(rightRecord)])
  for (const key of keys) {
    const leftValue = leftRecord[key]
    const rightValue = rightRecord[key]
    if (Object.is(leftValue, rightValue)) continue
    if (leftValue instanceof Date && rightValue instanceof Date && leftValue.getTime() === rightValue.getTime())
      continue
    return false
  }
  return true
}
